import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/core/actions/dropdown-menu";
import Badge from "@/components/core/badge";
import { Button } from "@/components/core/buttons/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/core/card";
import { DataTable } from "@/components/core/data-table";
import { Input } from "@/components/core/forms/input";
import { useModal } from "@/components/core/modals/use-modal";
import { useUpcomingInvoiceDueDatesQuery } from "@/features/dashboard/api/queries/contract-invoice-query";
import { useSalesContractDashboardQuery, useSalesContractsQuery } from "@/features/dashboard/api/queries/sales-contract-query";
import type { SalesContract } from "@/features/dashboard/types/sales-contract";
import { HasRole, useAuth } from "@/hooks/use-auth";
import {
	exportMonthlyTrendToPDF,
	exportSalesDashboardToPDF,
	exportToCSV,
	exportToXLSX,
	prepareMonthlyTrendDataForExport,
	prepareSalesDashboardDataForExport,
} from "@/utils/export-data";
import {
	IconAlertCircle,
	IconCalendar,
	IconCalendarEvent,
	IconCircleCheck,
	IconClock,
	IconCoin,
	IconFileExport,
	IconFileSpreadsheet,
	IconFileTypeCsv,
	IconFileTypePdf,
	IconTrendingUp,
	IconUsers,
} from "@tabler/icons-react";
import { endOfYear, format, startOfMonth, startOfYear } from "date-fns";
import type React from "react";
import { useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AdminSearch } from "./admin-search";
import { ExpiredContracts } from "./expired-contracts";
import { ExpiringContracts } from "./expiring-contracts";
import { PendingInvoicesModal } from "./pending-invoices-modal";
import { ContractsBreakdownModal } from "./recurring-contracts-modal";
import { SalespersonDetailsModal } from "./salesperson-details-modal";
import { UpcomingInvoiceDueDates } from "./upcoming-invoice-due-dates";

export const SalesContractDashboard: React.FC = () => {
	const { user, hasRole } = useAuth();
	const [searchTerm, setSearchTerm] = useState("");
	const [searchResults, setSearchResults] = useState<Record<string, unknown> | null>(null);
	const [upcomingEndDatesSearchResults, setUpcomingEndDatesSearchResults] = useState<Record<string, unknown> | null>(null);
	const [clearSearchTrigger, setClearSearchTrigger] = useState(false);

	// Date filter state - separate form values from applied filter values
	const currentDate = new Date();
	const defaultDateFrom = format(startOfYear(currentDate), "yyyy-MM-dd");
	const defaultDateTo = format(endOfYear(currentDate), "yyyy-MM-dd");

	// Form input values (what user types)
	const [formDateFrom, setFormDateFrom] = useState(defaultDateFrom);
	const [formDateTo, setFormDateTo] = useState(defaultDateTo);

	// Applied filter values (what gets sent to API)
	const [filterDateFrom, setFilterDateFrom] = useState(defaultDateFrom);
	const [filterDateTo, setFilterDateTo] = useState(defaultDateTo);

	// For salesperson users, filter to only their own contracts
	const salespersonId = hasRole(["salesperson"]) && !hasRole(["admin"]) ? user?.id : undefined;

	const { data, isLoading, error } = useSalesContractDashboardQuery(filterDateFrom, filterDateTo, salespersonId);

	// Query for upcoming invoice due dates (15 days)
	const { data: upcomingInvoiceDueDates } = useUpcomingInvoiceDueDatesQuery(15, salespersonId);

	const modal = useModal();

	// Handle search results from AdminSearch
	const handleSearchResults = (results: Record<string, unknown>) => {
		setSearchResults(results);
	};

	// Handle upcoming end dates search results from AdminSearch
	const handleUpcomingEndDatesSearchResults = (results: Record<string, unknown>) => {
		setUpcomingEndDatesSearchResults(results);
	};

	// Clear search results when search term is cleared
	const handleSearchChange = (term: string) => {
		setSearchTerm(term);
		if (!term) {
			setSearchResults(null);
			setUpcomingEndDatesSearchResults(null);
		}
	};

	// Calculate metrics from search results
	const getSearchMetrics = () => {
		if (!searchResults?.data) return null;

		const contracts = searchResults.data;

		// Helper function to safely parse any numeric value
		const safeParseNumber = (value: unknown): number => {
			if (value === null || value === undefined || value === "") return 0;
			const parsed = typeof value === "string" ? Number.parseFloat(value) : Number(value);
			return Number.isNaN(parsed) ? 0 : parsed;
		};

		// Helper function to safely parse monthly recurring sales
		const getMonthlyRecurringSales = (contract: Record<string, unknown>) => {
			return safeParseNumber(contract.monthly_recurring_sales);
		};

		// Helper function to safely parse contract amount
		const getContractAmount = (contract: Record<string, unknown>) => {
			return safeParseNumber(contract.contract_amount);
		};

		// Helper function to get revenue value (one-time or recurring)
		const getRevenueValue = (contract: Record<string, unknown>) => {
			const monthlyRecurring = getMonthlyRecurringSales(contract);
			const contractAmount = getContractAmount(contract);

			// If monthly_recurring_sales is 0, it's a one-time project
			if (monthlyRecurring === 0 && contractAmount > 0) {
				return contractAmount;
			}
			// Otherwise it's recurring
			return monthlyRecurring;
		};

		// Calculate total invoice amounts from search results
		const totalInvoiceAmount = contracts.reduce((sum: number, contract: Record<string, unknown>) => {
			return sum + safeParseNumber(contract.invoice_total_amount);
		}, 0);

		// Calculate collected amount from invoice data
		const collectedAmount = contracts.reduce((sum: number, contract: Record<string, unknown>) => {
			return sum + safeParseNumber(contract.invoice_collected_amount);
		}, 0);

		// Calculate pending amount from invoice data
		const pendingAmount = contracts.reduce((sum: number, contract: Record<string, unknown>) => {
			return sum + safeParseNumber(contract.invoice_pending_amount);
		}, 0);

		const activeCount = contracts.filter((contract: Record<string, unknown>) => contract.status === "active").length;

		// Calculate total royalty amount from search results
		const totalRoyaltyAmount = contracts.reduce((sum: number, contract: Record<string, unknown>) => {
			return sum + safeParseNumber(contract.royalty_amount);
		}, 0);

		const metrics = {
			total_revenue: totalInvoiceAmount, // Use total invoice amounts from search
			collected_amount: collectedAmount, // Use collected invoice totals
			pending_collection: pendingAmount, // Use pending invoice totals
			active_contracts_count: activeCount,
			total_royalty_amount: totalRoyaltyAmount,
		};
		return metrics;
	};

	const handleApplyFilter = () => {
		setFilterDateFrom(formDateFrom);
		setFilterDateTo(formDateTo);
	};

	const handleResetToCurrentMonth = () => {
		const currentDate = new Date();
		const dateFrom = format(startOfMonth(currentDate), "yyyy-MM-dd");
		// To = today, not end-of-month — don't show future-dated period
		const dateTo = format(currentDate, "yyyy-MM-dd");

		setFormDateFrom(dateFrom);
		setFormDateTo(dateTo);
		setFilterDateFrom(dateFrom);
		setFilterDateTo(dateTo);
	};

	const handleClearFilter = () => {
		const clearedFrom = "2010-01-01";
		const clearedTo = defaultDateTo;
		setFormDateFrom(clearedFrom);
		setFormDateTo(clearedTo);
		setFilterDateFrom(clearedFrom);
		setFilterDateTo(clearedTo);

		// Clear admin search state
		setSearchTerm("");
		setSearchResults(null);
		setUpcomingEndDatesSearchResults(null);

		// Trigger AdminSearch component to clear
		setClearSearchTrigger(true);
		// Reset trigger immediately to allow future clears
		setTimeout(() => setClearSearchTrigger(false), 0);
	};

	// Use search metrics when search is active, otherwise use dashboard data
	const searchMetrics = getSearchMetrics();

	// Helper function to safely parse dashboard metric values
	const safeMetricValue = (value: unknown): number => {
		const parsed = Number(value) || 0;
		return Number.isNaN(parsed) ? 0 : parsed;
	};

	const displayMetrics = searchMetrics || {
		total_revenue: safeMetricValue(data?.total_revenue),
		collected_amount: safeMetricValue(data?.collected_amount),
		pending_collection: safeMetricValue(data?.pending_collection),
		active_contracts_count: safeMetricValue(data?.active_contracts_count),
		total_royalty_amount: safeMetricValue(data?.total_royalty_amount),
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-96">
				<div className="text-gray-500">Loading dashboard...</div>
			</div>
		);
	}

	if (error || !data) {
		return (
			<div className="flex items-center justify-center h-96">
				<div className="text-red-500">Failed to load dashboard data</div>
			</div>
		);
	}

	const formatCurrency = (value: number) => {
		return new Intl.NumberFormat("en-PH", {
			style: "currency",
			currency: "PHP",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(value);
	};

	const handleSalespersonClick = (salesperson: Record<string, unknown>) => {
		modal.openModal({
			title: "Salesperson Details",
			description: "Detailed breakdown of sales performance",
			component: <SalespersonDetailsModal salesperson={salesperson} />,
		});
	};

	const handlePendingBarClick = (monthData: { month: string }) => {
		modal.openModal({
			title: `Pending Invoices — ${monthData.month}`,
			description: "Invoices with pending collection status",
			component: <PendingInvoicesModal month={monthData.month} salespersonId={salespersonId ? String(salespersonId) : undefined} />,
			size: "xl",
		});
	};

	const handleExport = async (format: "csv" | "xlsx" | "pdf") => {
		if (!data?.sales_by_salesperson || data.sales_by_salesperson.length === 0) {
			return;
		}

		const { headers, rows } = prepareSalesDashboardDataForExport(data);
		const dateRange = `${filterDateFrom}_to_${filterDateTo}`;
		const filename = `sales_dashboard_${dateRange}_${new Date().toISOString().split("T")[0]}`;

		switch (format) {
			case "csv":
				exportToCSV({ headers, rows, filename });
				break;
			case "xlsx":
				exportToXLSX({ headers, rows, filename });
				break;
			case "pdf":
				await exportSalesDashboardToPDF({ headers, rows, filename });
				break;
		}
	};

	const handleMonthlyTrendExport = async (format: "csv" | "xlsx" | "pdf") => {
		if (!data?.monthly_trend || data.monthly_trend.length === 0) {
			return;
		}

		const { headers, rows } = prepareMonthlyTrendDataForExport(data.monthly_trend);
		const dateRange = `filtered_${filterDateFrom}_to_${filterDateTo}`;
		const filename = `monthly_recurring_sales_trend_${dateRange}_${new Date().toISOString().split("T")[0]}`;

		switch (format) {
			case "csv":
				exportToCSV({ headers, rows, filename });
				break;
			case "xlsx":
				exportToXLSX({ headers, rows, filename });
				break;
			case "pdf":
				await exportMonthlyTrendToPDF({ headers, rows, filename });
				break;
		}
	};

	return (
		<div className="space-y-6">
			{/* Date Filter */}
			<Card>
				<CardContent className="pt-6">
					<div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
						<div className="flex items-center gap-2">
							<IconCalendar className="w-5 h-5 text-gray-500" />
							<span className="text-sm font-medium text-gray-700">Filter by Contract Start Date:</span>
						</div>
						<div className="flex items-center gap-4">
							<div className="flex items-center gap-2">
								<label htmlFor="date-from" className="text-sm text-gray-600">
									From:
								</label>
								<Input id="date-from" type="date" value={formDateFrom} onChange={(e) => setFormDateFrom(e.target.value)} className="w-auto" />
							</div>
							<div className="flex items-center gap-2">
								<label htmlFor="date-to" className="text-sm text-gray-600">
									To:
								</label>
								<Input id="date-to" type="date" value={formDateTo} onChange={(e) => setFormDateTo(e.target.value)} className="w-auto" />
							</div>
							<Button onClick={handleApplyFilter} size="sm" className="bg-blue-600 hover:bg-blue-700">
								Filter
							</Button>
							<Button variant="outline" onClick={handleResetToCurrentMonth} size="sm">
								This Month
							</Button>
							<Button variant="outline" onClick={handleClearFilter} size="sm" className="text-gray-600 hover:text-gray-800">
								Clear Filter
							</Button>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Admin Search - Admin only */}
			<HasRole roles={["admin"]}>
				<AdminSearch
					className="bg-blue-100"
					onSearchChange={handleSearchChange}
					onSearchResults={handleSearchResults}
					onUpcomingEndDatesResults={handleUpcomingEndDatesSearchResults}
					dateFilter={`${filterDateFrom} to ${filterDateTo}`}
					dateFrom={filterDateFrom}
					dateTo={filterDateTo}
					clearSearch={clearSearchTrigger}
					salespersonId={salespersonId}
				/>
			</HasRole>

			{/* Key Metrics */}
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Total Invoice Amount</CardTitle>
						<IconTrendingUp className="h-4 w-4 text-indigo-500" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-indigo-600">{formatCurrency(displayMetrics.total_revenue)}</div>
						<p className="text-xs text-muted-foreground">{searchMetrics ? "Search results invoices total" : "Total invoice amounts"}</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Collected Invoices</CardTitle>
						<IconCircleCheck className="h-4 w-4 text-green-500" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-green-600">{formatCurrency(displayMetrics.collected_amount)}</div>
						<p className="text-xs text-muted-foreground">{searchMetrics ? "Search results collected" : "Invoices marked as collected"}</p>
					</CardContent>
				</Card>

				<Card
					className="cursor-pointer hover:shadow-md transition-shadow"
					onClick={() => {
						modal.openModal({
							title: "Pending Collection",
							description: "All invoices with pending collection status",
							component: (
								<PendingInvoicesModal dateFrom={filterDateFrom} dateTo={filterDateTo} salespersonId={salespersonId ? String(salespersonId) : undefined} />
							),
							size: "xl",
						});
					}}
				>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Pending Collection</CardTitle>
						<IconClock className="h-4 w-4 text-yellow-500" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-yellow-600">{formatCurrency(displayMetrics.pending_collection)}</div>
						<p className="text-xs text-muted-foreground">{searchMetrics ? "Search results pending" : "Invoices pending collection"}</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Total Royalty Amount</CardTitle>
						<IconCoin className="h-4 w-4 text-amber-500" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-amber-600">{formatCurrency(displayMetrics.total_royalty_amount)}</div>
						<p className="text-xs text-muted-foreground">{searchMetrics ? "Search results royalty" : "Total royalty amounts"}</p>
					</CardContent>
				</Card>

				<Card
					className="cursor-pointer hover:shadow-md transition-shadow"
					onClick={() => {
						modal.openModal({
							title: "Total Projects",
							description: "Projects whose start date falls within the selected range",
							size: "xl",
							component: (
								<ContractsBreakdownModal
									type="projects"
									dateFrom={filterDateFrom || undefined}
									dateTo={filterDateTo || undefined}
									salespersonId={salespersonId ? String(salespersonId) : undefined}
								/>
							),
						});
					}}
				>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Total Projects</CardTitle>
						<IconUsers className="h-4 w-4 text-blue-500" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-blue-600">{data.projects_count ?? 0}</div>
						<p className="text-xs text-muted-foreground">Click to view projects</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Active Contracts</CardTitle>
						<IconCalendarEvent className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{displayMetrics.active_contracts_count}</div>
						<p className="text-xs text-muted-foreground">{searchMetrics ? "Active in search results" : "Currently active"}</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Expiring Soon</CardTitle>
						<IconAlertCircle className="h-4 w-4 text-orange-500" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-orange-600">{data.expiring_soon_count || 0}</div>
						<p className="text-xs text-muted-foreground">Within 30 days</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Expired</CardTitle>
						<IconAlertCircle className="h-4 w-4 text-red-500" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-red-600">{data.expired_count || 0}</div>
						<p className="text-xs text-muted-foreground">Contracts expired</p>
					</CardContent>
				</Card>
			</div>

			{/* Charts Row */}
			<div className="grid grid-cols-1 gap-6">
				{/* Monthly Trend Chart - Full width */}
				<Card>
					<CardHeader>
						<div className="flex items-center justify-between">
							<CardTitle>Sales Trend - Collection Status</CardTitle>
							{data.monthly_trend && data.monthly_trend.length > 0 && (
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button variant="outline" size="sm">
											<IconFileExport className="w-3 h-3 mr-1" />
											Export
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end">
										<DropdownMenuItem onClick={() => handleMonthlyTrendExport("csv")}>
											<IconFileTypeCsv className="w-4 h-4 mr-2" />
											Export as CSV
										</DropdownMenuItem>
										<DropdownMenuItem onClick={() => handleMonthlyTrendExport("xlsx")}>
											<IconFileSpreadsheet className="w-4 h-4 mr-2" />
											Export as Excel
										</DropdownMenuItem>
										<DropdownMenuItem onClick={() => handleMonthlyTrendExport("pdf")}>
											<IconFileTypePdf className="w-4 h-4 mr-2" />
											Export as PDF
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							)}
						</div>
					</CardHeader>
					<CardContent>
						<ResponsiveContainer width="100%" height={400}>
							<BarChart data={data.monthly_trend}>
								<CartesianGrid strokeDasharray="3 3" />
								<XAxis dataKey="month" />
								<YAxis tickFormatter={(value) => formatCurrency(value)} />
								<Tooltip
									formatter={(value: number, name: string) => {
										let displayName = name;
										if (name === "sales") displayName = "Total Sales";
										else if (name === "collected") displayName = "Collected";
										else if (name === "pending") displayName = "Pending";

										return [formatCurrency(value), displayName];
									}}
								/>
								<Legend />
								<Bar dataKey="sales" fill="#3b82f6" name="Total Sales" opacity={0.8} />
								<Bar dataKey="collected" fill="#10b981" name="Collected" opacity={0.8} />
								<Bar
									dataKey="pending"
									fill="#f59e0b"
									name="Pending"
									opacity={0.8}
									className="cursor-pointer"
									onClick={(entry) => {
										if (entry?.month) handlePendingBarClick(entry as { month: string });
									}}
								/>
							</BarChart>
						</ResponsiveContainer>
					</CardContent>
				</Card>
			</div>

			{/* Sales by Salesperson - Admin only */}
			<HasRole roles={["admin"]}>
				<Card>
					<CardHeader>
						<div className="flex items-center justify-between">
							<CardTitle>Sales by Salesperson (Click for details)</CardTitle>
							{data.sales_by_salesperson && data.sales_by_salesperson.length > 0 && (
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button variant="outline" size="sm">
											<IconFileExport className="w-3 h-3 mr-1" />
											Export
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end">
										<DropdownMenuItem onClick={() => handleExport("csv")}>
											<IconFileTypeCsv className="w-4 h-4 mr-2" />
											Export as CSV
										</DropdownMenuItem>
										<DropdownMenuItem onClick={() => handleExport("xlsx")}>
											<IconFileSpreadsheet className="w-4 h-4 mr-2" />
											Export as Excel
										</DropdownMenuItem>
										<DropdownMenuItem onClick={() => handleExport("pdf")}>
											<IconFileTypePdf className="w-4 h-4 mr-2" />
											Export as PDF
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							)}
						</div>
					</CardHeader>
					<CardContent>
						<ResponsiveContainer width="100%" height={300}>
							<BarChart data={data.sales_by_salesperson} onClick={(data) => data && handleSalespersonClick(data.activePayload?.[0]?.payload)}>
								<CartesianGrid strokeDasharray="3 3" />
								<XAxis dataKey="salesperson.name" />
								<YAxis tickFormatter={(value) => formatCurrency(value)} />
								<Tooltip formatter={(value: number) => formatCurrency(value)} cursor={{ fill: "rgba(59, 130, 246, 0.1)" }} />
								<Bar dataKey="total_monthly_sales" fill="#3b82f6" />
							</BarChart>
						</ResponsiveContainer>
					</CardContent>
				</Card>
			</HasRole>

			{/* Upcoming Invoice Due Dates */}
			<UpcomingInvoiceDueDates invoices={upcomingInvoiceDueDates?.data || []} className="mt-6" />

			{/* Contracts Expiring Within 30 Days */}
			<ExpiringContracts contracts={data.expiring_soon || []} className="mt-6" />

			{/* Expired Contracts */}
			<ExpiredContracts contracts={data.expired || []} className="mt-6" />
		</div>
	);
};
